import { Button } from "@/components/ui/button";
import { ArrowLeft, User, Shield, MessageCircle, Eye, CheckCircle, Clock, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useSEO } from "@/hooks/useSEO";

export default function Vision() {
  const [scrolled, setScrolled] = useState(false);
  const heroRef = useRef<HTMLElement | null>(null);
  const [heroInView, setHeroInView] = useState(false);
  const [heroProgress, setHeroProgress] = useState(0);
  const cardRef = useRef<HTMLElement | null>(null);
  const [cardInView, setCardInView] = useState(false);
  const bannerRef = useRef<HTMLElement | null>(null);
  const [bannerInView, setBannerInView] = useState(false);
  const ctaRef = useRef<HTMLElement | null>(null);
  const [ctaInView, setCtaInView] = useState(false);
  const featuresRef = useRef<HTMLElement | null>(null);
  const [activeSection, setActiveSection] = useState<'hero'|'statement'|'features'|'banner'|'cta'>('hero');

  useSEO({
    title: "Our Vision - Nomera Health Employment Identity Platform",
    description: "Discover how Nomera Health is building the future of healthcare employment with persistent profiles, secure document management, and verified work history.",
    keywords: "healthcare employment, identity platform, professional profiles, healthcare careers, employment verification"
  });

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Observe hero entrance
  useEffect(() => {
    if (!heroRef.current) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) setHeroInView(true);
      });
    }, { threshold: 0.35 });
    obs.observe(heroRef.current);
    return () => obs.disconnect();
  }, []);

  // Observe trust banner
  useEffect(() => {
    if (!bannerRef.current) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) setBannerInView(true); });
    }, { threshold: 0.35 });
    obs.observe(bannerRef.current);
    return () => obs.disconnect();
  }, []);

  // Update activeSection using one observer
  useEffect(() => {
    const sections: {key: typeof activeSection, el: HTMLElement | null}[] = [
      { key: 'hero', el: heroRef.current as any },
      { key: 'statement', el: cardRef.current as any },
      { key: 'features', el: featuresRef.current as any },
      { key: 'banner', el: bannerRef.current as any },
      { key: 'cta', el: ctaRef.current as any },
    ];
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          const found = sections.find((s) => s.el === e.target);
          if (found) setActiveSection(found.key);
        }
      });
    }, { threshold: 0.5 });
    sections.forEach((s)=> s.el && io.observe(s.el));
    return () => io.disconnect();
  }, []);

  const smoothScrollTo = (ref: React.RefObject<HTMLElement>) => {
    const el = ref.current;
    if (!el) return;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  };

  // Observe final CTA
  useEffect(() => {
    if (!ctaRef.current) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) setCtaInView(true); });
    }, { threshold: 0.33 });
    obs.observe(ctaRef.current);
    return () => obs.disconnect();
  }, []);

  // Subtle scroll-based opacity for radials (map 0-220px -> +20%)
  useEffect(() => {
    const mq = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mq && mq.matches) { setHeroProgress(1); return; }
    const onScroll = () => {
      const y = Math.max(0, window.scrollY);
      const prog = Math.min(1, y / 220);
      setHeroProgress(prog);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Observe statement card
  useEffect(() => {
    if (!cardRef.current) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) setCardInView(true);
      });
    }, { threshold: 0.4 });
    obs.observe(cardRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Floating progress dots */}
      <nav className="hidden md:flex flex-col gap-3 fixed right-4 top-1/2 -translate-y-1/2 z-40" aria-label="Section progress">
        {([
          {k:'hero', ref: heroRef},
          {k:'statement', ref: cardRef},
          {k:'features', ref: featuresRef},
          {k:'banner', ref: bannerRef},
          {k:'cta', ref: ctaRef},
        ] as const).map((s) => (
          <button key={s.k as string} onClick={() => smoothScrollTo(s.ref as any)} aria-label={`Go to ${s.k}`} className={`w-3 h-3 rounded-full transition ${activeSection===s.k? 'bg-black/70 scale-110' : 'bg-black/20 hover:bg-black/40'}`} />
        ))}
      </nav>
      {/* Ambient background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl animate-pulse-glow"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-secondary/5 rounded-full blur-3xl animate-pulse-glow" style={{ animationDelay: '1.5s' }}></div>
      </div>

      {/* Header – glassy, layered, consistent with landing */}
      <header role="banner" className="sticky top-0 z-50">
        <div
          className={`relative backdrop-blur-md transition-all duration-300 ${
            scrolled ? 'shadow-[0_6px_20px_rgba(0,0,0,.06)]' : ''
          }`}
          style={{ backdropFilter: 'blur(8px)', backgroundColor: scrolled ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.75)' }}
        >
          <style>{`
            @keyframes nxr-logo-in { from { opacity:0; transform: scale(.98) } to { opacity:1; transform: scale(1) } }
            @keyframes nxr-logo-breathe { 0%,100% { transform: scale(1) } 50% { transform: scale(1.01) } }
            .nxr-logo { animation: nxr-logo-in .6s ease both, nxr-logo-breathe 6s ease-in-out infinite; }
          `}</style>
          {/* ambient blobs */}
          <div className="pointer-events-none absolute inset-0 -z-10">
            <div className="absolute -left-10 top-0 w-40 h-24 rounded-full blur-3xl" style={{ background:'rgba(163,207,162,.10)' }} />
            <div className="absolute -right-8 top-2 w-40 h-24 rounded-full blur-3xl" style={{ background:'rgba(155,143,193,.10)' }} />
          </div>
          {/* bottom soft glow */}
          <div className="absolute left-0 right-0 -bottom-1 h-3" style={{ background:'linear-gradient(to bottom, rgba(0,0,0,.06), rgba(0,0,0,0))' }} />

          <div className="max-w-6xl mx-auto px-6 h-16 md:h-18 flex items-center justify-between text-neutral-800">
            <Link to="/" className="flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-black/40">
              <img src="/lovable-uploads/Favicon.png" alt="Nomera Health Logo" className="w-8 h-8 rounded-xl nxr-logo" />
              <span className="text-lg font-bold">Nomera Health</span>
            </Link>
            <div className="flex items-center gap-3">
              <Link to="/" className="inline-flex items-center gap-2 text-neutral-700 hover:text-neutral-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-black/40">
                <ArrowLeft className="w-4 h-4" /> Back to Home
              </Link>
              <Link to="/auth">
                <Button variant="outline" size="sm" className="rounded-xl bg-white/70 hover:bg-white text-neutral-800">Sign In</Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-20 sm:pt-32 pb-16">
        {/* Vision Hero */}
        <section ref={heroRef} aria-labelledby="vision-hero-title" className="relative w-full overflow-hidden py-24 md:py-28" style={{ background: 'linear-gradient(180deg, #FAF6F0 0%, #FFFFFF 72%)' }}>
          <style>{`
            .nxr-shimmer { background: linear-gradient(90deg, transparent, rgba(163,207,162,.35), rgba(155,143,193,.35), transparent); animation: nxr-shimmer 6s linear infinite; opacity:.12 }
            @keyframes nxr-shimmer { 0% { transform: translateX(-8%); opacity:.10 } 50% { transform: translateX(8%); opacity:.14 } 100% { transform: translateX(-8%); opacity:.10 } }
            @keyframes tightenTrack { 0% { letter-spacing: .02em } 100% { letter-spacing: 0em } }
            @media(prefers-reduced-motion:reduce){ .nxr-shimmer { animation: none } }
          `}</style>
          {/* texture overlay behind content */}
          <div className="absolute inset-0 pointer-events-none -z-10" aria-hidden>
            {/* stronger soft radials */}
            <div className="absolute left-1/2 -translate-x-1/2 -top-24 w-[40vw] h-[40vw] max-w-[48rem] max-h-[48rem] rounded-full" style={{ filter:'blur(240px)', background:'rgba(163,207,162,0.14)', opacity: 1 + 0.2*heroProgress }} />
            <div className="absolute right-[8%] top-6 w-[28vw] h-[28vw] max-w-[20rem] max-h-[20rem] rounded-full" style={{ filter:'blur(200px)', background:'rgba(155,143,193,0.10)', opacity: 1 + 0.2*heroProgress }} />
            {/* linen */}
            <div className="absolute inset-0" style={{ backgroundImage:'radial-gradient(rgba(0,0,0,.04) 1px, transparent 1px)', backgroundSize:'6px 6px', opacity:.04 }} />
            <div className="nxr-shimmer absolute left-0 right-0 top-[55%] h-px" />
            {/* faint top vignette */}
            <div className="absolute left-0 right-0 top-0 h-[30vh]" style={{ background:'linear-gradient(to bottom, rgba(0,0,0,.06), rgba(0,0,0,0))' }} />
          </div>
          <div className="max-w-6xl mx-auto px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <h1 id="vision-hero-title" className="text-4xl md:text-6xl font-bold text-neutral-900 mb-4" style={{ opacity: heroInView ? 1 : 0, transform: heroInView ? 'translateY(0px)' : 'translateY(12px)', transition: 'opacity 280ms ease-out, transform 280ms ease-out', animation: heroInView ? 'tightenTrack 400ms ease 800ms forwards' : undefined }}>Our Vision</h1>
              <p className="text-base md:text-lg text-neutral-600" style={{ opacity: heroInView ? 1 : 0, transform: heroInView ? 'translateY(0px)' : 'translateY(8px)', transition: 'opacity 260ms ease-out 120ms, transform 260ms ease-out 120ms' }}>Building the future of healthcare employment</p>
            </div>
          </div>
          {/* down arrow prompt */}
          <button type="button" onClick={() => smoothScrollTo(cardRef)} className="absolute left-1/2 -translate-x-1/2 bottom-6 flex flex-col items-center text-neutral-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-black/40">
            <span className="text-xs mb-1 opacity-75">Scroll to Explore</span>
            <ChevronDown className="w-5 h-5 animate-bounce-slow" />
            <style>{`@keyframes bounce-slow{0%,100%{ transform: translateY(0) }50%{ transform: translateY(4px) }} .animate-bounce-slow{ animation: bounce-slow 1.8s ease-in-out infinite }`}</style>
          </button>
        </section>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">

          {/* Main Vision Statement */}
          <section
            ref={cardRef}
            aria-labelledby="vision-main-card-title"
            className="relative w-full py-16 md:py-20 bg-transparent mb-10"
          >
            <style>{`
              @keyframes particleFloat { 0%{ transform: translate3d(0,0,0) } 50%{ transform: translate3d(12px,-10px,0) } 100%{ transform: translate3d(0,0,0) } }
              .nxr-particle { border-radius:9999px; opacity:.08; animation: particleFloat 50s linear infinite; }
              @media(prefers-reduced-motion:reduce){ .nxr-particle { animation: none !important } }
            `}</style>
            {/* Background accents: radials + particles */}
            <div className="absolute inset-0 pointer-events-none" aria-hidden>
              <div className="absolute -top-10 -left-10 w-64 h-64 rounded-full blur-3xl" style={{ background:'radial-gradient(closest-side, rgba(163,207,162,0.20), transparent 70%)' }} />
              <div className="absolute -bottom-6 -right-8 w-72 h-72 rounded-full blur-3xl" style={{ background:'radial-gradient(closest-side, rgba(155,143,193,0.18), transparent 70%)' }} />
              {/* particles */}
              <div className="absolute inset-0">
                {[{t:10,l:15,s:14,c:'#A3CFA2',d:'0s'},{t:20,l:70,s:18,c:'#9B8FC1',d:'6s'},{t:65,l:25,s:16,c:'#A3CFA2',d:'12s'},{t:42,l:50,s:20,c:'#9B8FC1',d:'18s'},{t:78,l:60,s:12,c:'#A3CFA2',d:'24s'},{t:30,l:82,s:22,c:'#9B8FC1',d:'30s'},{t:55,l:8,s:14,c:'#A3CFA2',d:'36s'},{t:85,l:35,s:18,c:'#9B8FC1',d:'42s'}].map((p,idx)=> (
                  <span key={idx} className="nxr-particle absolute" style={{ top:`${p.t}%`, left:`${p.l}%`, width:p.s, height:p.s, background:p.c, animationDelay:p.d }} />
                ))}
              </div>
            </div>

            <div className="max-w-4xl mx-auto px-6 relative z-10">
              <div
                className="rounded-2xl bg-white/65 backdrop-blur-md shadow-lg p-8 md:p-12 transition will-change-transform hover:scale-[1.005]"
                style={{ opacity: cardInView ? 1 : 0, transform: cardInView ? 'translateY(0px)' : 'translateY(8px)', transition: 'opacity 350ms ease-out, transform 350ms ease-out' }}
              >
                <div className="absolute inset-0 rounded-2xl pointer-events-none" style={{ padding:1, WebkitMaskImage:'-webkit-radial-gradient(white, black)' }} aria-hidden>
                  <div className="absolute inset-0 rounded-2xl" style={{ background:'linear-gradient(90deg, rgba(163,207,162,.18), rgba(155,143,193,.18))', boxShadow:'inset 0 1px 0 rgba(255,255,255,.65), 0 20px 50px rgba(0,0,0,.08)' }} />
                </div>
                {/* passport/check glyph */}
                <svg className="absolute left-5 top-5 w-6 h-6 opacity-40" viewBox="0 0 24 24" fill="none" aria-hidden style={{ opacity:.04, animation:'glyphBreath 12s ease-in-out infinite' }}>
                  <circle cx="12" cy="12" r="9" stroke="#2b2b2b" strokeWidth="1.2" />
                  <path d="M7 12l3 3 6-6" stroke="#2b2b2b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <style>{`@keyframes glyphBreath{0%,100%{opacity:.04}50%{opacity:.08}}`}</style>
                <h3 id="vision-main-card-title" className="text-2xl md:text-3xl font-semibold text-neutral-900 mb-4" style={{ opacity: cardInView ? 1 : 0, transform: cardInView ? 'translateY(0px)' : 'translateY(6px)', transition: 'opacity 300ms ease-out 80ms, transform 300ms ease-out 80ms' }}>
                  More Than Just a Hiring Tool
                </h3>
                <p className="text-base md:text-lg text-neutral-600 leading-relaxed" style={{ opacity: cardInView ? 1 : 0, transition: 'opacity 240ms ease-out 180ms' }}>
                  Nomera Health is not just a hiring tool — it's a long-term employment identity platform.
                  It allows candidates to build a persistent profile that evolves with their career,
                  creating a trusted employment passport for the healthcare industry.
                </p>
              </div>
            </div>
          </section>

          {/* Features Grid */}
          <section ref={featuresRef} aria-label="Vision features" className="relative w-full overflow-hidden py-14 md:py-18 mb-16">
            <style>{`
              @keyframes fadeSlideUp { from{ opacity:0; transform: translateY(10px) } to{ opacity:1; transform: translateY(0) } }
              @keyframes iconPop { from{ transform: scale(.96) } to{ transform: scale(1) } }
              .nxr-fcard { opacity:0; transform: translateY(10px); }
              .nxr-ficon { transform: scale(.96); }
              .nxr-show .nxr-fcard { animation: fadeSlideUp 300ms ease-out forwards }
              .nxr-show .nxr-ficon { animation: iconPop 220ms ease-out forwards }
              @media(prefers-reduced-motion:reduce){ .nxr-fcard{ transform:none } .nxr-show .nxr-fcard{ animation: none; opacity:1 } .nxr-show .nxr-ficon{ animation: none; transform:none } }
            `}</style>
            {/* ambient behind grid */}
            <div className="absolute inset-0 -z-10 pointer-events-none" aria-hidden>
              <div className="absolute -top-8 -left-10 w-72 h-72 rounded-full blur-3xl" style={{ background:'rgba(163,207,162,.12)' }} />
              <div className="absolute -bottom-8 -right-10 w-80 h-80 rounded-full blur-3xl" style={{ background:'rgba(155,143,193,.10)' }} />
            </div>
            <div ref={(el)=>{ /* simple inView trigger */ if(el){ const io=new IntersectionObserver(es=>{ es.forEach(e=>{ if(e.isIntersecting){ el.classList.add('nxr-show'); io.disconnect(); } }); },{threshold:.35}); io.observe(el);} }} className="max-w-6xl mx-auto px-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Persistent Profile - green */}
                <article aria-labelledby="vfeat-pp" className="group relative min-h-[200px] h-full rounded-2xl border bg-white/80 backdrop-blur shadow-sm p-6 md:p-7 focus-within:ring-2 focus-within:ring-black/10 transition transform hover:scale-[1.01] hover:shadow-md">
                  <div className="absolute inset-0 rounded-2xl pointer-events-none" style={{ padding:1 }} aria-hidden>
                    <div className="absolute inset-0 rounded-2xl opacity-90 group-hover:opacity-100" style={{ background:'linear-gradient(90deg, rgba(163,207,162,.18), rgba(155,143,193,.18))' }} />
                  </div>
                  {/* tiny vine */}
                  <svg className="absolute left-3 bottom-3 w-12 h-6 opacity-10" viewBox="0 0 120 60" fill="none" aria-hidden>
                    <path d="M0 50 C 30 30, 70 40, 120 10" stroke="#7FB17D" strokeWidth="2" />
                  </svg>
                  <div className="flex items-start gap-4">
                    <div className="nxr-ficon h-12 w-12 rounded-xl flex items-center justify-center bg-[rgba(163,207,162,.15)]">
                      <User className="w-6 h-6" style={{ color:'#A3CFA2' }} aria-hidden="true" />
                    </div>
                    <div>
                      <h3 id="vfeat-pp" className="text-lg md:text-xl font-semibold text-neutral-900">Persistent Professional Profile</h3>
                      <p className="mt-2.5 text-neutral-600 leading-relaxed group-hover:-translate-y-px transition">Candidates maintain a consistent application profile, reducing repetitive data entry and ensuring their professional story follows them throughout their career journey.</p>
                    </div>
                  </div>
                </article>

                {/* Secure Documentation - purple */}
                <article aria-labelledby="vfeat-sd" className="group relative min-h-[200px] h-full rounded-2xl border bg-white/80 backdrop-blur shadow-sm p-6 md:p-7 focus-within:ring-2 focus-within:ring-black/10 transition transform hover:scale-[1.01] hover:shadow-md">
                  <div className="absolute inset-0 rounded-2xl pointer-events-none" style={{ padding:1 }} aria-hidden>
                    <div className="absolute inset-0 rounded-2xl opacity-90 group-hover:opacity-100" style={{ background:'linear-gradient(90deg, rgba(163,207,162,.18), rgba(155,143,193,.18))' }} />
                  </div>
                  {/* network nodes */}
                  <svg className="absolute right-3 bottom-3 w-12 h-8 opacity-10" viewBox="0 0 120 60" fill="none" aria-hidden>
                    <circle cx="20" cy="40" r="3" fill="#9B8FC1" /><circle cx="60" cy="20" r="3" fill="#9B8FC1" /><circle cx="100" cy="10" r="3" fill="#9B8FC1" /><path d="M20 40 L60 20 L100 10" stroke="#9B8FC1" strokeWidth="1.5" />
                  </svg>
                  <div className="flex items-start gap-4">
                    <div className="nxr-ficon h-12 w-12 rounded-xl flex items-center justify-center" style={{ background:'rgba(155,143,193,.15)' }}>
                      <Shield className="w-6 h-6" style={{ color:'#9B8FC1' }} aria-hidden="true" />
                    </div>
                    <div>
                      <h3 id="vfeat-sd" className="text-lg md:text-xl font-semibold text-neutral-900">Secure Document Management</h3>
                      <p className="mt-2.5 text-neutral-600 leading-relaxed group-hover:-translate-y-px transition">Certifications, licenses, and onboarding documents are securely stored and carried forward to future opportunities, streamlining career transitions.</p>
                    </div>
                  </div>
                </article>

                {/* Verified Reviews - green */}
                <article aria-labelledby="vfeat-vw" className="group relative min-h-[200px] h-full rounded-2xl border bg-white/80 backdrop-blur shadow-sm p-6 md:p-7 focus-within:ring-2 focus-within:ring-black/10 transition transform hover:scale-[1.01] hover:shadow-md">
                  <div className="absolute inset-0 rounded-2xl pointer-events-none" style={{ padding:1 }} aria-hidden>
                    <div className="absolute inset-0 rounded-2xl opacity-90 group-hover:opacity-100" style={{ background:'linear-gradient(90deg, rgba(163,207,162,.18), rgba(155,143,193,.18))' }} />
                  </div>
                  <svg className="absolute left-3 bottom-3 w-12 h-6 opacity-10" viewBox="0 0 120 60" fill="none" aria-hidden>
                    <path d="M0 50 C 30 30, 70 40, 120 10" stroke="#7FB17D" strokeWidth="2" />
                  </svg>
                  <div className="flex items-start gap-4">
                    <div className="nxr-ficon h-12 w-12 rounded-xl flex items-center justify-center bg-[rgba(163,207,162,.15)]">
                      <MessageCircle className="w-6 h-6" style={{ color:'#A3CFA2' }} aria-hidden="true" />
                    </div>
                    <div>
                      <h3 id="vfeat-vw" className="text-lg md:text-xl font-semibold text-neutral-900">Verified Work History</h3>
                      <p className="mt-2.5 text-neutral-600 leading-relaxed mb-3 group-hover:-translate-y-px transition">Past employers can leave verified reviews or performance notes with candidate consent, building a trusted employment record that showcases professional growth.</p>
                      <span className="inline-flex items-center rounded-full bg-neutral-100 text-neutral-600 text-xs px-3 py-1 border border-neutral-200">Coming Soon</span>
                    </div>
                  </div>
                </article>

                {/* Employer Insights - purple */}
                <article aria-labelledby="vfeat-ei" className="group relative min-h-[200px] h-full rounded-2xl border bg-white/80 backdrop-blur shadow-sm p-6 md:p-7 focus-within:ring-2 focus-within:ring-black/10 transition transform hover:scale-[1.01] hover:shadow-md">
                  <div className="absolute inset-0 rounded-2xl pointer-events-none" style={{ padding:1 }} aria-hidden>
                    <div className="absolute inset-0 rounded-2xl opacity-90 group-hover:opacity-100" style={{ background:'linear-gradient(90deg, rgba(163,207,162,.18), rgba(155,143,193,.18))' }} />
                  </div>
                  <svg className="absolute right-3 bottom-3 w-12 h-8 opacity-10" viewBox="0 0 120 60" fill="none" aria-hidden>
                    <circle cx="20" cy="40" r="3" fill="#9B8FC1" /><circle cx="60" cy="20" r="3" fill="#9B8FC1" /><circle cx="100" cy="10" r="3" fill="#9B8FC1" /><path d="M20 40 L60 20 L100 10" stroke="#9B8FC1" strokeWidth="1.5" />
                  </svg>
                  <div className="flex items-start gap-4">
                    <div className="nxr-ficon h-12 w-12 rounded-xl flex items-center justify-center" style={{ background:'rgba(155,143,193,.15)' }}>
                      <Eye className="w-6 h-6" style={{ color:'#9B8FC1' }} aria-hidden="true" />
                    </div>
                    <div>
                      <h3 id="vfeat-ei" className="text-lg md:text-xl font-semibold text-neutral-900">Enhanced Employer Insights</h3>
                      <p className="mt-2.5 text-neutral-600 leading-relaxed group-hover:-translate-y-px transition">Employers gain deeper insights through work history, document validity, and prior feedback — helping ensure better-fit hires and reducing turnover.</p>
                    </div>
                  </div>
                </article>
              </div>
            </div>
          </section>

          {/* Tagline Banner */}
          <section ref={bannerRef} aria-labelledby="trust-banner" className="relative w-full overflow-hidden py-8 md:py-10">
            <style>{`
              .nxr-line-shimmer{ background:linear-gradient(90deg, transparent, rgba(163,207,162,.15), rgba(155,143,193,.15), transparent); animation:nxr-line 6s linear infinite; opacity:.12 }
              @keyframes nxr-line{0%{ transform: translateX(-8%) }50%{ transform: translateX(8%) }100%{ transform: translateX(-8%) }}
              @keyframes pop{from{ transform: scale(.96)} to{ transform: scale(1)} }
              @media(prefers-reduced-motion:reduce){ .nxr-line-shimmer{ animation:none } }
            `}</style>
            {/* ambient blobs */}
            <div className="absolute inset-0 -z-10 pointer-events-none" aria-hidden>
              <div className="absolute -top-6 -left-10 w-64 h-64 rounded-full blur-3xl" style={{ background:'rgba(163,207,162,.12)' }} />
              <div className="absolute -bottom-6 -right-10 w-72 h-72 rounded-full blur-3xl" style={{ background:'rgba(155,143,193,.10)' }} />
              <div className="absolute inset-0" style={{ backgroundImage:'radial-gradient(rgba(0,0,0,.04) 1px, transparent 1px)', backgroundSize:'6px 6px', opacity:.04 }} />
            </div>
            <div className="max-w-6xl mx-auto px-6">
              <div
                className="relative rounded-2xl px-5 md:px-8 py-5 md:py-6 flex items-center gap-3 md:gap-4 bg-white/70 backdrop-blur-md shadow-lg transition will-change-transform group hover:shadow-[0_18px_36px_rgba(0,0,0,.09)]"
                style={{ opacity: bannerInView ? 1 : 0, transform: bannerInView ? 'translateY(0)' : 'translateY(10px)', transition: 'opacity 260ms ease-out, transform 260ms ease-out' }}
              >
                {/* gradient hairline border */}
                <div className="absolute inset-0 rounded-2xl pointer-events-none" style={{ padding:1 }} aria-hidden>
                  <div className="absolute inset-0 rounded-2xl opacity-90 group-hover:opacity-100" style={{ background:'linear-gradient(90deg, rgba(163,207,162,.18), rgba(155,143,193,.18))' }} />
                </div>
                {/* shimmer accent */}
                <div className="nxr-line-shimmer absolute left-3 right-3 top-1/2 -translate-y-1/2 h-px rounded" aria-hidden />
                {/* icon badge */}
                <div className="h-10 w-10 md:h-11 md:w-11 rounded-full flex items-center justify-center" style={{ background:'rgba(163,207,162,.18)', animation: bannerInView ? 'pop 180ms ease-out both' : undefined }}>
                  <CheckCircle className="w-5 h-5 md:w-6 md:h-6" style={{ color:'#2b7a58' }} aria-hidden="true" />
                </div>
                <p id="trust-banner" className="text-neutral-800 md:text-lg font-medium">Your trusted employment passport for the healthcare industry</p>
              </div>
            </div>
          </section>

          {/* Final CTA */}
          <section ref={ctaRef} aria-labelledby="final-cta" className="relative w-full overflow-hidden mt-24" style={{ background:'linear-gradient(96deg, #F4FBF7 0%, #EAE7F6 100%)' }}>
            <style>{`
              @keyframes bokehFloat { 0%{ transform: scale(1.02) } 100%{ transform: scale(1) } }
              .nxr-cta-shape { animation: bokehFloat 8s ease-in-out infinite alternate; }
              .nxr-cta-title-in { letter-spacing: .5px; }
              @keyframes tighten { to { letter-spacing: 0px } }
              .nxr-cta-btn { background: linear-gradient(90deg, #7ECF9A 0%, #4FAE78 100%); transition: transform .15s ease, box-shadow .15s ease, filter .15s ease; }
              .nxr-cta-btn:hover { filter: brightness(1.05); box-shadow: 0 14px 28px rgba(79,174,120,.2); }
              .nxr-cta-btn:active { transform: scale(.97); }
              @media(prefers-reduced-motion:reduce){ .nxr-cta-shape { animation:none } }
            `}</style>
            {/* glows + grid */}
            <div className="absolute inset-0 -z-10 pointer-events-none" aria-hidden>
              <div className="absolute -top-10 -left-10 w-80 h-80 rounded-full blur-4xl nxr-cta-shape" style={{ background:'rgba(163,207,162,.14)' }} />
              <div className="absolute -bottom-10 -right-10 w-96 h-96 rounded-full blur-4xl nxr-cta-shape" style={{ background:'rgba(155,143,193,.14)' }} />
              <div className="absolute inset-0" style={{ backgroundImage:'repeating-linear-gradient(135deg, rgba(0,0,0,.04) 0 1px, transparent 1px 12px)', opacity:.04 }} />
            </div>
            <section className="mx-auto max-w-2xl px-6 text-center py-[clamp(3rem,10vh,8rem)]">
              <h1 id="final-cta" className="text-3xl md:text-5xl font-semibold tracking-tight text-neutral-900 nxr-cta-title-in" style={{ opacity: ctaInView ? 1 : 0, transform: ctaInView ? 'translateY(0)' : 'translateY(14px)', transition:'opacity 300ms ease-out, transform 300ms ease-out', animation: ctaInView ? 'tighten 500ms ease 800ms forwards' : undefined }}>Ready to Get Started?</h1>

              {/* Optional short subheading helps fill space on large screens */}
              <p className="mt-4 text-lg text-neutral-600">
                Join a curated network of healthcare employers and talent.
              </p>

              <div className="mt-8">
                <Link to="/auth" aria-label="Join Nomera Health">
                  <Button size="lg" className="nxr-cta-btn rounded-xl px-8 py-4 text-white font-semibold">Join Nomera Health</Button>
                </Link>
              </div>
            </section>
          </section>
        </div>
      </main>
    </div>
  );
}